// 수량 칸의 식(`=평*3+2`)을 재귀 하강으로 계산한다 — eval·Function 을 쓰지 않는다.

import { pyeongToM2 } from "./area-unit";
import { QTY_MAX } from "./schema";

/** 오류 종류. 화면은 `message` 를 그대로 보이고, 분기는 `kind` 로만 가른다. */
export type QtyExprErrorKind =
  | "empty"
  | "syntax"
  | "paren"
  | "div_zero"
  | "no_area"
  | "negative"
  | "range";

export type QtyExprResult =
  | { ok: true; value: number }
  | { ok: false; kind: QtyExprErrorKind; message: string };

export interface QtyExprVars {
  /** 평수. null·0 이하이면 「평」 을 쓸 수 없다(0 으로 계산하지 않는다). */
  area_pyeong: number | null;
  /** 면적 원본(m², #173) — `m2`·`㎡` 의 값 · 없으면 평×3.305785. */
  area_m2?: number | null;
}

// 수량의 유효 자리는 DB `numeric(14,3)` 에 맞춘 소수 셋째까지다.
const QTY_SCALE = 1_000;

// 한 칸에 적을 식의 길이·괄호와 단항 부호를 합친 중첩 깊이 상한. 넘으면 스택이 넘치기 전에 끊는다.
export const QTY_EXPR_MAX_LENGTH = 200;
export const QTY_EXPR_MAX_DEPTH = 64;

const MESSAGES: Record<QtyExprErrorKind, string> = {
  empty: "식이 비어 있습니다",
  syntax: "식을 읽을 수 없습니다",
  paren: "괄호가 맞지 않습니다",
  div_zero: "0 으로 나눌 수 없습니다",
  no_area: "평수가 없어 「평」 을 계산할 수 없습니다",
  negative: "수량은 0 이상이어야 합니다",
  range: "수량이 너무 큽니다 — 자릿수를 확인해 주세요",
};

// `m2` 를 못 풀 때의 문구 — 종류는 평과 같은 no_area 다.
const NO_AREA_M2 = "면적이 없어 「m²」 를 계산할 수 없습니다";

class ExprError extends Error {
  constructor(readonly kind: QtyExprErrorKind, message?: string) {
    super(message ?? MESSAGES[kind]);
  }
}

type Token =
  | { t: "num"; v: number }
  | { t: "area" }
  | { t: "m2" }
  | { t: "op"; v: "+" | "-" | "*" | "/" }
  | { t: "lp" }
  | { t: "rp" };

const OPS: Record<string, "+" | "-" | "*" | "/"> = {
  "+": "+",
  "-": "-",
  "*": "*",
  "×": "*",
  x: "*",
  X: "*",
  "/": "/",
  "÷": "/",
};

// 앞의 `=`·쉼표·공백을 걷어 낸 본문.
const cleanOf = (raw: string): string =>
  raw.trim().replace(/^=/, "").replace(/[,\s]/g, "");

const tokenize = (text: string): Token[] => {
  const tokens: Token[] = [];
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    const num = /^(\d+\.?\d*|\.\d+)/.exec(text.slice(i));
    if (num) {
      tokens.push({ t: "num", v: Number(num[0]) });
      i += num[0].length;
    } else if (ch === "평") {
      tokens.push({ t: "area" });
      i += 1;
    } else if (ch === "㎡") {
      tokens.push({ t: "m2" });
      i += 1;
    } else if (/^[mM][2²]/.test(text.slice(i, i + 2))) {
      tokens.push({ t: "m2" });
      i += 2;
    } else if (ch in OPS) {
      tokens.push({ t: "op", v: OPS[ch] });
      i += 1;
    } else if (ch === "(") {
      tokens.push({ t: "lp" });
      i += 1;
    } else if (ch === ")") {
      tokens.push({ t: "rp" });
      i += 1;
    } else {
      throw new ExprError("syntax");
    }
  }
  return tokens;
};

// expr := term (('+'|'-') term)* · term := unary (('*'|'/') unary)* · unary := '-' unary | primary
const parse = (tokens: Token[], vars: QtyExprVars): number => {
  let pos = 0;
  let depth = 0;
  const peek = (): Token | undefined => tokens[pos];
  const nested = <T>(fn: () => T): T => {
    depth += 1;
    if (depth > QTY_EXPR_MAX_DEPTH) throw new ExprError("syntax");
    const value = fn();
    depth -= 1;
    return value;
  };

  const primary = (): number => {
    const tok = peek();
    if (!tok) throw new ExprError("syntax");
    pos += 1;
    if (tok.t === "num") return tok.v;
    if (tok.t === "area") {
      const area = vars.area_pyeong;
      if (area === null || !Number.isFinite(area) || area <= 0) {
        throw new ExprError("no_area");
      }
      return area;
    }
    if (tok.t === "m2") {
      const m2 = vars.area_m2 ?? pyeongToM2(vars.area_pyeong);
      if (m2 === null || !Number.isFinite(m2) || m2 <= 0) throw new ExprError("no_area", NO_AREA_M2);
      return m2;
    }
    if (tok.t === "lp") {
      const value = nested(expr);
      if (peek()?.t !== "rp") throw new ExprError("paren");
      pos += 1;
      return value;
    }
    if (tok.t === "rp") throw new ExprError("paren");
    throw new ExprError("syntax");
  };

  const unary = (): number => {
    const tok = peek();
    if (tok?.t === "op" && (tok.v === "-" || tok.v === "+")) {
      pos += 1;
      const value = nested(unary);
      return tok.v === "-" ? -value : value;
    }
    return primary();
  };

  const term = (): number => {
    let value = unary();
    for (;;) {
      const tok = peek();
      if (tok?.t !== "op" || (tok.v !== "*" && tok.v !== "/")) return value;
      pos += 1;
      const rhs = unary();
      if (tok.v === "*") {
        value *= rhs;
      } else {
        if (rhs === 0) throw new ExprError("div_zero");
        value /= rhs;
      }
    }
  };

  const expr = (): number => {
    let value = term();
    for (;;) {
      const tok = peek();
      if (tok?.t !== "op" || (tok.v !== "+" && tok.v !== "-")) return value;
      pos += 1;
      const rhs = term();
      value = tok.v === "+" ? value + rhs : value - rhs;
    }
  };

  const value = expr();
  if (pos < tokens.length) {
    throw new ExprError(tokens[pos].t === "rp" ? "paren" : "syntax");
  }
  return value;
};

/** 식을 계산해 소수 셋째 자리로 반올림한다. 음수·0 나누기·평수 없음은 오류로 돌려준다. */
export const evalQtyExpr = (raw: string, vars: QtyExprVars): QtyExprResult => {
  const text = cleanOf(raw);
  if (text === "") return { ok: false, kind: "empty", message: MESSAGES.empty };
  if (text.length > QTY_EXPR_MAX_LENGTH) {
    return { ok: false, kind: "syntax", message: MESSAGES.syntax };
  }
  try {
    const value = parse(tokenize(text), vars);
    if (!Number.isFinite(value)) throw new ExprError("range");
    const rounded = Math.round(value * QTY_SCALE) / QTY_SCALE;
    // -0.0001 처럼 반올림해 0 이 되는 값은 0 으로 본다.
    if (rounded < 0) throw new ExprError("negative");
    if (rounded > QTY_MAX) throw new ExprError("range");
    return { ok: true, value: rounded === 0 ? 0 : rounded };
  } catch (error) {
    if (error instanceof ExprError) {
      return { ok: false, kind: error.kind, message: error.message };
    }
    // 예상 못 한 예외(스택 넘침 등)도 화면을 죽이지 않고 못 읽은 식으로 돌려준다.
    return { ok: false, kind: "syntax", message: MESSAGES.syntax };
  }
};

/** 단순 숫자(쉼표 허용)가 아니면 식이다. 빈 칸은 식이 아니다. */
export const isExpr = (raw: string): boolean => {
  const trimmed = raw.trim();
  if (trimmed === "") return false;
  if (trimmed.startsWith("=")) return true;
  const text = trimmed.replace(/[,\s]/g, "");
  return !/^(\d+\.?\d*|\.\d+)$/.test(text);
};

/** 식이 면적 변수(`평`·`m2`·`㎡`)를 쓰는지. */
export const usesArea = (raw: string): boolean => /평|㎡|[mM][2²]/.test(raw);
